import { useEffect, useState } from "react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Award, TrendingDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const FIELDS = [["weight", "Weight (kg)"], ["body_fat", "Body Fat %"], ["waist", "Waist (cm)"], ["chest", "Chest (cm)"], ["arms", "Arms (cm)"], ["thigh", "Thigh (cm)"], ["hips", "Hips (cm)"]];

export default function Progress() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => { api.get("/me/progress").then((r) => setEntries(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {};
    Object.entries(form).forEach(([k, v]) => { if (v !== "" && v != null) payload[k] = k === "photo_url" ? v : Number(v); });
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one value."); return; }
    setBusy(true);
    try { await api.post("/me/progress", payload); toast.success("Progress logged!"); setForm({}); setOpen(false); load(); }
    catch { toast.error("Could not save."); } finally { setBusy(false); }
  };

  const chart = entries.filter((e) => e.weight != null).map((e) => ({ date: e.date.slice(5), weight: e.weight }));
  const first = chart[0]?.weight, last = chart[chart.length - 1]?.weight;
  const delta = first && last ? (last - first).toFixed(1) : null;
  const photos = entries.filter((e) => e.photo_url);

  const badges = [
    { earned: (user.points || 0) >= 10, icon: Award, t: "First Workout" },
    { earned: entries.length >= 1, icon: TrendingDown, t: "First Log" },
    { earned: (user.points || 0) >= 50, icon: Award, t: "50 Points" },
    { earned: (user.challenges || []).length >= 1, icon: Award, t: "Challenger" },
  ];

  return (
    <div data-testid="progress-page" className="px-4 md:px-8 pb-16 pt-2">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">Progress</h1><p className="text-muted-foreground mt-1">Track your numbers, photos and wins over time.</p></div>
        <button onClick={() => setOpen(true)} data-testid="progress-add" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-xs uppercase tracking-[0.12em] font-bold hover:scale-[1.02] transition-transform"><Plus size={16} /> Log Entry</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 border border-border rounded-xl bg-card p-6" data-testid="progress-chart">
          <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-4">Weight Trend {delta && <span className={`text-sm ml-2 ${delta <= 0 ? "text-primary" : "text-muted-foreground"}`}>{delta > 0 ? "+" : ""}{delta} kg</span>}</h3>
          {chart.length > 1 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chart} margin={{ left: -20, right: 10, top: 10 }}>
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="h-[260px] grid place-items-center text-muted-foreground text-sm">Log at least two weight entries to see your trend.</div>}
        </div>
        <div className="border border-border rounded-xl bg-card p-6" data-testid="badges-card">
          <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-4">Badges</h3>
          <div className="grid grid-cols-2 gap-3">
            {badges.map((b) => (
              <div key={b.t} className={`rounded-lg border p-4 text-center ${b.earned ? "border-primary bg-primary/5" : "border-border opacity-50"}`}>
                <b.icon size={26} className={`mx-auto ${b.earned ? "text-primary" : "text-muted-foreground"}`} />
                <div className="text-xs font-bold mt-2">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {photos.length > 0 && (
        <div className="mt-6">
          <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-4">Progress Photos</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {photos.map((p) => (<div key={p.id} className="rounded-xl overflow-hidden border border-border"><img src={p.photo_url} alt={p.date} className="w-full aspect-[3/4] object-cover" /><div className="text-xs text-muted-foreground p-2">{p.date}</div></div>))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="progress-dialog">
          <DialogHeader><DialogTitle className="font-display font-800 uppercase text-3xl tracking-tight">Log Progress</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2 max-h-[60vh] overflow-y-auto">
            {FIELDS.map(([k, l]) => (
              <div key={k}><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{l}</Label><Input type="number" data-testid={`prog-${k}`} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-2 h-11" /></div>
            ))}
            <div className="col-span-2"><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Photo URL (optional)</Label><Input data-testid="prog-photo" value={form.photo_url ?? ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="mt-2 h-11" placeholder="https://…" /></div>
          </div>
          <button onClick={save} disabled={busy} data-testid="progress-save" className="w-full bg-primary text-primary-foreground py-3.5 rounded-full text-xs uppercase tracking-[0.12em] font-bold disabled:opacity-60">{busy ? "Saving…" : "Save Entry"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
